if (!customElements.get('tab-interface')) {
  /**
   * A custom element that implements a tabbed interface.
   * @class TabInterface
   * @extends HTMLElement
   */
  class TabInterface extends HTMLElement {
    constructor() {
      super();

      this.tabs = [];
      this.firstTab = null;
      this.lastTab = null;
      this.activeTab = null;
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role=tab]'));

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('keydown', this.onKeydown.bind(this));
        tab.addEventListener('click', this.onClick.bind(this));

        if (!this.firstTab) {
          this.firstTab = tab;
          this.activeTab = tab;
        }
        this.lastTab = tab;

        if (i === this.tabs.length - 1) {
          this.setSelectedTab(this.activeTab);
        }
      });
    }

    /**
     * Sets the selected tab.
     * @param currentTab
     * @returns {void}
     */
    setSelectedTab(currentTab) {
      this.activeTab = currentTab;
      const tabpanels = Array.from(this.querySelectorAll('[role=tabpanel]'));

      this.tabs.forEach((tab, i) => {
        if (tabpanels[i] && currentTab && currentTab.getAttribute('aria-controls') === tabpanels[i].getAttribute('id')) {
          tab.setAttribute('aria-selected', 'true');
          tab.removeAttribute('tabindex');
          tab.classList.add('active-tab');
          if (tabpanels[i] !== undefined && tabpanels[i].classList.contains('is-hidden')) {
            tabpanels[i].classList.remove('is-hidden');
          }
          tabpanels[i].setAttribute('aria-hidden', 'false');
          const event = new CustomEvent('tabChange', { detail: { tabpanel: tabpanels[i] } });
          this.dispatchEvent(event);
        } else if (tabpanels[i]) {
          tab.setAttribute('aria-selected', 'false');
          tab.classList.remove('active-tab');
          tab.tabIndex = -1;

          tabpanels[i].classList.add('is-hidden');
          tabpanels[i].setAttribute('aria-hidden', 'true');
        }
      });
    }

    /**
     * Moves focus to the specified tab.
     * @param currentTab
     * @returns {void}
     */
    moveFocusToTab(currentTab) {
      currentTab.focus();
    }

    /**
     * Moves focus to the previous tab.
     * @param currentTab
     * @returns {void}
     */
    moveFocusToPreviousTab(currentTab) {
      let index;

      if (currentTab === this.firstTab) {
        this.moveFocusToTab(this.lastTab);
      } else {
        index = this.tabs.indexOf(currentTab);
        this.moveFocusToTab(this.tabs[index - 1]);
      }
    }

    /**
     * Moves focus to the next tab.
     * @param currentTab
     * @returns {void}
     */
    moveFocusToNextTab(currentTab) {
      let index;

      if (currentTab === this.lastTab) {
        this.moveFocusToTab(this.firstTab);
      } else {
        index = this.tabs.indexOf(currentTab);
        this.moveFocusToTab(this.tabs[index + 1]);
      }
    }

    /* EVENT HANDLERS */

    /**
     * Handles keydown events for tabs.
     * @param event
     * @returns {void}
     */
    onKeydown(event) {
      var tgt = event.currentTarget,
        flag = false;

      switch (event.key) {
        case 'ArrowLeft':
          this.moveFocusToPreviousTab(tgt);
          flag = true;
          break;

        case 'ArrowRight':
          this.moveFocusToNextTab(tgt);
          flag = true;
          break;

        case 'Home':
          this.moveFocusToTab(this.firstTab);
          flag = true;
          break;

        case 'End':
          this.moveFocusToTab(this.lastTab);
          flag = true;
          break;

        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    /**
     * Handles click events for tabs.
     * @param event
     * @returns {void}
     */
    onClick(event) {
      this.setSelectedTab(event.currentTarget);
    }
  }

  customElements.define('tab-interface', TabInterface);
}
